// studentapps/TodoApp.js
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  IconButton,
  Chip,
  Paper,
  Grid,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CorporateFare as CorporateIcon
} from '@mui/icons-material';

const TodoApp = () => {
  const [todos, setTodos] = useState([
    { id: 1, text: 'Complete math assignment', completed: true, priority: 'high', dueDate: '2024-01-15' },
    { id: 2, text: 'Study for physics test', completed: false, priority: 'medium', dueDate: '2024-01-18' },
    { id: 3, text: 'Read chapter 5 of chemistry', completed: false, priority: 'low', dueDate: '2024-01-20' },
    { id: 4, text: 'Prepare presentation slides', completed: false, priority: 'high', dueDate: '2024-01-16' }
  ]);

  const [newTodo, setNewTodo] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');

  const addTodo = () => {
    if (newTodo.trim()) {
      const todo = {
        id: Date.now(),
        text: newTodo,
        completed: false,
        priority: priority,
        dueDate: dueDate || new Date().toISOString().split('T')[0]
      };
      setTodos([...todos, todo]);
      setNewTodo('');
      setPriority('medium');
      setDueDate('');
    }
  };

  const toggleTodo = (id) => {
    setTodos(todos.map(todo =>
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    ));
  };

  const deleteTodo = (id) => {
    setTodos(todos.filter(todo => todo.id !== id));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const pendingTodos = todos.filter(todo => !todo.completed);
  const completedTodos = todos.filter(todo => todo.completed);

  const highPriorityCount = todos.filter(t => t.priority === 'high' && !t.completed).length;
  const totalTasks = todos.length;
  const completedCount = completedTodos.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="700" gutterBottom>
            To-Do List
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Stay organized and productive
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label="Powered by Zolang Nexora" 
            size="small" 
            color="primary" 
            variant="outlined"
            icon={<CorporateIcon />}
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {/* Add Todo */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Add New Task
              </Typography>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="What needs to be done?"
                    value={newTodo}
                    onChange={(e) => setNewTodo(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTodo()}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    size="small"
                    label="Priority"
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    fullWidth
                    type="date"
                    variant="outlined"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    size="small"
                    label="Due Date"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <Button
                    variant="contained"
                    onClick={addTodo}
                    fullWidth
                    sx={{ minHeight: '40px' }}
                  >
                    <AddIcon />
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Todo Lists */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Pending Tasks ({pendingTodos.length})
                  </Typography>
                  <List>
                    {pendingTodos.map((todo) => (
                      <Paper key={todo.id} sx={{ mb: 1, borderRadius: 2 }}>
                        <ListItem>
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={todo.completed}
                              onChange={() => toggleTodo(todo.id)}
                              color="primary"
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={todo.text}
                            secondary={`Due: ${todo.dueDate}`}
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              label={todo.priority}
                              size="small"
                              color={getPriorityColor(todo.priority)}
                              sx={{ mr: 1 }}
                            />
                            <IconButton
                              edge="end"
                              onClick={() => deleteTodo(todo.id)}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </Paper>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="success.main">
                    Completed ({completedTodos.length})
                  </Typography>
                  <List>
                    {completedTodos.map((todo) => (
                      <Paper key={todo.id} sx={{ mb: 1, borderRadius: 2, bgcolor: 'success.light' }}>
                        <ListItem>
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={todo.completed}
                              onChange={() => toggleTodo(todo.id)}
                              color="success"
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={todo.text}
                            sx={{ textDecoration: 'line-through', color: 'success.dark' }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => deleteTodo(todo.id)}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </Paper>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Task Statistics
              </Typography>
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="primary" fontWeight="700">
                  {completedCount}/{totalTasks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tasks Completed
                </Typography>
              </Box>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Priority Breakdown:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">High Priority</Typography>
                    <Chip label={highPriorityCount} size="small" color="error" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Medium Priority</Typography>
                    <Chip label={todos.filter(t => t.priority === 'medium' && !t.completed).length} size="small" color="warning" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Low Priority</Typography>
                    <Chip label={todos.filter(t => t.priority === 'low' && !t.completed).length} size="small" color="success" />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Quick Actions:
                </Typography>
                <Button variant="outlined" fullWidth sx={{ mb: 1 }}>
                  Export Tasks
                </Button>
                <Button variant="outlined" fullWidth>
                  Set Reminders
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TodoApp;